import { Avatar, Badge, Box, CircularProgress } from "@mui/material";
import React, { Component } from "react";
import Theme from "../common/Theme";
import AvatarSource from "./AvatarSource";
import VacancyIcon from "../resources/icons-user.svg";
import ComplianceBadge from "./ComplianceBadge";
import Tooltip from "./Tooltip";

interface ProfilePictureProps {
  user: any;
  updating?: boolean;
  size: keyof typeof ProfilePicture.size;
  // When defined, use this as the <img> src instead of the cached photo for user.employeeId (see
  // avatarStore). `null` forces the initials fallback (e.g. while the malware scan is still
  // processing). `undefined` reads the shared avatar cache.
  imageUrl?: string | null;
  onClick?: () => void;
  sx?: object;
  tooltip?: string;
  badgeContent?: React.ReactNode;
  showCompliance?: boolean;
  complianceBadgeSize?: string | number;
  complianceBadgeMargin?: string;
  showTooltip?: boolean;
}

export default class ProfilePicture extends Component<ProfilePictureProps> {
  static size = { xs: "32px", sm: "40px", md: "44px", lg: "64px", xl: "80px" };
  static fontSize = {
    xs: "14px",
    sm: "16px",
    md: "16px",
    lg: "18px",
    xl: "20px",
  };

  static defaultProps = {
    size: "xl",
    showCompliance: false,
    showTooltip: true,
  };

  renderAvatar() {
    const { user, imageUrl } = this.props;

    // imageUrl override (CurrentUserAvatar): use it verbatim. Otherwise read the shared avatar cache.
    if (imageUrl !== undefined) return this.renderAvatarImage(imageUrl);

    return (
      <AvatarSource employeeId={user?.employeeId} enabled={!!user?.hasProfilePicture}>
        {(src) => this.renderAvatarImage(src)}
      </AvatarSource>
    );
  }

  renderAvatarImage(src: string | null) {
    const { user, updating, size, onClick, sx } = this.props;
    const showPic = !!src && !updating;

    return (
      <Avatar
        src={(showPic && src) || undefined}
        sx={{
          width: ProfilePicture.size[size],
          height: ProfilePicture.size[size],
          background: Theme.getStyleVar("--prussian-blue-pb-500"),
          ...sx,
        }}
        onClick={onClick}
      >
        {updating ? (
          <CircularProgress />
        ) : !user || user.isVacancy ? (
          <div>
            <VacancyIcon />
          </div>
        ) : (
          !showPic && (
            <Box
              className="klayo_profilepic-initials"
              sx={{ fontSize: ProfilePicture.fontSize[size] }}
            >
              {user.getInitials()}
            </Box>
          )
        )}
      </Avatar>
    );
  }

  renderAvatarWithTooltip() {
    const { user, tooltip, showTooltip } = this.props;

    return (tooltip || user) && showTooltip ? (
      <Tooltip title={tooltip ? tooltip : user ? user.fullName : null}>
        {this.renderAvatar()}
      </Tooltip>
    ) : (
      this.renderAvatar()
    );
  }

  render() {
    const {
      user,
      size,
      sx,
      badgeContent,
      showCompliance,
      complianceBadgeSize,
      complianceBadgeMargin,
      showTooltip,
    } = this.props;

    return (
      <div
        className={
          "klayo_profilepic klayo_profilepic--size-" +
          size +
          (!user || user.isVacancy ? " klayo_profilepic-vacancy" : "")
        }
        style={{
          width: ProfilePicture.size[size],
          height: ProfilePicture.size[size],
          backgroundColor: Theme.getStyleVar("--prussian-blue-pb-500"),
          ...sx,
        }}
      >
        {showCompliance ? (
          <ComplianceBadge
            isCompliant={user.isCompliant}
            size={complianceBadgeSize}
            margin={complianceBadgeMargin}
            showTooltip={showTooltip}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              width: ProfilePicture.size[size],
              height: ProfilePicture.size[size],
            }}
          >
            {this.renderAvatar()}
          </ComplianceBadge>
        ) : (
          <Badge
            badgeContent={badgeContent}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              width: ProfilePicture.size[size],
              height: ProfilePicture.size[size],
            }}
          >
            {this.renderAvatar()}
          </Badge>
        )}
      </div>
    );
  }
}
